import React, { createElement } from 'react';
import { XIcon, AlertTriangleIcon, PackageIcon, DownloadIcon, RefreshCwIcon } from 'lucide-react';
import { Alert } from '../data/alertsData';
interface AlertDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  alert: Alert | null;
}
export function AlertDetailModal({
  isOpen,
  onClose,
  alert
}: AlertDetailModalProps) {
  if (!isOpen || !alert) return null;
  const handleDownloadExcel = () => {
    const headers = ['Reference', 'Design', 'Desired Stock', 'Current Stock', 'Incoming', 'Shortage', 'Priority'];
    const rows = alert.references.map(item => [item.reference, item.design, item.desiredStock.toString(), item.currentStock.toString(), item.incoming.toString(), Math.abs(item.difference).toString(), item.priority.toUpperCase()]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alert-${alert.id}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const getSeverityColor = () => {
    switch (alert.severity) {
      case 'critical':
        return 'from-red-500 to-red-600';
      case 'high':
        return 'from-orange-500 to-orange-600';
      case 'medium':
        return 'from-yellow-500 to-yellow-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };
  const totalShortage = alert.references.reduce((sum, item) => sum + Math.abs(item.difference), 0);
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-11">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-12">
        <div className={`bg-gradient-to-r ${getSeverityColor()} text-white p-6 flex items-center justify-between`} data-id="element-13">
          <div className="flex items-center gap-4" data-id="element-14">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-15">
              <AlertTriangleIcon className="w-6 h-6" data-id="element-16" />
            </div>
            <div data-id="element-17">
              <h2 className="text-2xl font-bold" data-id="element-18">{alert.title}</h2>
              <p className="text-white/90 text-sm mt-1" data-id="element-19">
                {alert.references.length} references affected • Last update:{' '}
                {alert.lastUpdate}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors" data-id="element-20">
            <XIcon className="w-6 h-6" data-id="element-21" />
          </button>
        </div>
        <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-22">
          <div className="flex items-center justify-between" data-id="element-23">
            <div className="flex items-center gap-6" data-id="element-24">
              <div className="text-center" data-id="element-25">
                <p className="text-sm text-gray-600 mb-1" data-id="element-26">Total Shortage</p>
                <p className="text-3xl font-bold text-red-600" data-id="element-27">
                  {totalShortage}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-28">units</p>
              </div>
              <div className="w-px h-12 bg-gray-300" data-id="element-29"></div>
              <div className="text-center" data-id="element-30">
                <p className="text-sm text-gray-600 mb-1" data-id="element-31">Critical Items</p>
                <p className="text-3xl font-bold text-gray-900" data-id="element-32">
                  {alert.references.filter(i => i.priority === 'critical').length}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-33">
              <button onClick={handleDownloadExcel} className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-34">
                <DownloadIcon className="w-4 h-4" data-id="element-35" />
                Download Excel
              </button>
              <button className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-36">
                <RefreshCwIcon className="w-4 h-4" data-id="element-37" />
                Refresh
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6" data-id="element-38">
          <div className="space-y-3" data-id="element-39">
            {alert.references.map(item => <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all" data-id="element-40">
                <div className="flex items-start justify-between mb-4" data-id="element-41">
                  <div className="flex items-start gap-4 flex-1" data-id="element-42">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/30" data-id="element-43">
                      <PackageIcon className="w-6 h-6 text-white" data-id="element-44" />
                    </div>
                    <div className="flex-1" data-id="element-45">
                      <div className="flex items-center gap-3 mb-2" data-id="element-46">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-47">
                          {item.reference}
                        </h3>
                        <span className={`px-2.5 py-1 rounded text-xs font-semibold border ${getPriorityColor(item.priority)}`} data-id="element-48">
                          {item.priority.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-49">
                        {item.design}
                      </p>
                      <div className="grid grid-cols-5 gap-4" data-id="element-50">
                        <div data-id="element-51">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-52">Desired</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-53">
                            {item.desiredStock}
                          </p>
                        </div>
                        <div data-id="element-54">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-55">Current</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-56">
                            {item.currentStock}
                          </p>
                        </div>
                        <div data-id="element-57">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-58">Incoming</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-59">
                            {item.incoming}
                          </p>
                        </div>
                        <div data-id="element-60">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-61">Shortage</p>
                          <p className="text-lg font-bold text-red-600" data-id="element-62">
                            {Math.abs(item.difference)}
                          </p>
                        </div>
                        <div data-id="element-63">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-64">Coverage</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-65">
                            {Math.round(item.currentStock / item.desiredStock * 100)}
                            %
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-4 border-t border-gray-100" data-id="element-66">
                  <button className="flex-1 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded transition-colors border border-blue-200 text-sm font-medium" data-id="element-67">
                    View Details
                  </button>
                  <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded hover:from-blue-600 hover:to-blue-700 transition-all text-sm font-medium shadow-sm" data-id="element-68">
                    Add to Order
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}